<template>
  <div>
    <v-dialog
      v-model="show"
      @click:outside="closeSkillsEditorDialog"
      @keydown.esc="closeSkillsEditorDialog"
    >
      <v-card>
        <v-card-title>
          {{`${employee.employeeFullName}'s Set of Skills `}}
          <v-spacer></v-spacer>
          <v-btn
            icon
            blur
            @click.prevent="closeSkillsEditorDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row class="my-1 ml-2">
            <span class="english-level mr-2 d-flex align-items-center">English Level:</span>
            <span v-if="!editMode">{{englishLevelOfEmployee ? englishLevelOfEmployee.englishLevel : ''}}</span>
            <v-select
              v-else
              class="max-width-200"
              :items="englishLevels"
              item-text="Dev_Skills_Subcategory_Description"
              item-value="Dev_Skills_Subcategory_ID"
              v-model="englishLevelOfEmployee.subcategoryId"
              @change="englishLevelChange()"
              hide-detals
            ></v-select>
          </v-row>
          <v-data-table
            dense
            mobile-breakpoint="0"
            fixed-header
            :height="editorSkillsTableHeight"
            :headers="skillsHeaders"
            :items="employeeSkillsToEdit"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <template v-slot:[`item.isPrimary`]="{ item }">
              <span class="d-flex justify-content-center">
                <v-checkbox
                  :readonly="!editMode"
                  v-model="item.isPrimary"
                  :disabled="!editMode"
                ></v-checkbox>
              </span>
            </template>
            <template v-slot:[`item.Dev_Skills_Experience_Years`]="{ item }">
              <span v-if="!editMode"> {{ item.Dev_Skills_Experience_Years }} </span>
              <v-text-field
                v-else
                v-model="item.Dev_Skills_Experience_Years"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Dev_Skills_Experience_End_Year`]="{ item }">
              <span v-if="!editMode"> {{ item.Dev_Skills_Experience_End_Year }} </span>
              <v-text-field
                v-else
                v-model="item.Dev_Skills_Experience_End_Year"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Your_Estimation`]="{ item }">
              <span v-if="!editMode"> {{ item.Your_Estimation }} </span>
              <v-text-field
                v-else
                v-model="item.Your_Estimation"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Date_of_Estimation`]="{ item }">
              <span v-if="!editMode"> {{ item.Date_of_Estimation | dateFilter }} </span>
              <div v-else class="margin-top--20px">
                <DatePicker
                  v-model="item.Date_of_Estimation"
                ></DatePicker>
              </div>
            </template>
            <template v-slot:[`item.Expert_Estimation`]="{ item }">
              <span v-if="!editMode"> {{ item.Expert_Estimation }} </span>
              <v-select
                v-else
                v-model="item.Expert_Estimation"
                :items=[1,2,3,4,5]
                label="Expert Estimation"
              ></v-select>
            </template>
            <template v-slot:[`item.Expert_FullName`]="{ item }">
              <span v-if="!editMode"> {{ item.Expert_FullName }} </span>
              <v-select
                v-else-if="editMode && !item.Expert"
                v-model="item.Expert_ID"
                :items="employeeList"
                item-text="full_name"
                item-value="employee_id"
                @change="expertChange(item)"
                @clearable="clearExpert(item)"
              >
                <template v-slot:prepend-item>
                  <div class="px-2">
                    <v-text-field
                      label="Search employee"
                      v-model="searchEmployee"
                    ></v-text-field>
                  </div>
                </template>
              </v-select>
              <v-text-field
                v-else
                v-model="item.Expert_FullName"
              ></v-text-field>
            </template>
            <template v-slot:[`item.Expert`]="{ item }">
              <span class="d-flex justify-content-center">
                <v-checkbox
                  :readonly="!editMode"
                  :disabled="!editMode"
                  v-model="item.Expert"
                  @change="changeExpertMode(item)"
                ></v-checkbox>
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" small @click="saveSkillsGrid">{{editMode ? 'Save' : 'Edit'}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>

export default {
  name: 'EmployeeSkillsProfile',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: false
    },
    employee: {
      required: false,
      default: null
    }
  },
  data () {
    return {
      searchEmployee: '',
      editMode: false,
      employeeSkillsToEdit: [],
      editedEmployee: {
        employeeId: null,
        employeeFullName: null
      },
      englishLevelOfSelectedEmployee: {
        employeeId: null,
        categoryId: null,
        englishLevel: null,
        subcategoryId: null
      },
      englishLevels: null,
      skillsHeaders: [
        {
          text: 'Skill',
          value: 'Dev_Skills_Category_Description',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Sub-skill',
          value: 'Dev_Skills_Subcategory_Description',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Primary skill',
          value: 'isPrimary',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Experience, years',
          value: 'Dev_Skills_Experience_Years',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Finished work, year',
          value: 'Dev_Skills_Experience_End_Year',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Estimation',
          value: 'Your_Estimation',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Est. Date',
          value: 'Date_of_Estimation',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Expert Estimation',
          value: 'Expert_Estimation',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Expert Name',
          value: 'Expert_FullName',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Outside Expert',
          value: 'Expert',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ]
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    editorSkillsTableHeight () {
      const windowW = window.innerWidth
      return windowW < 1366 ? '400px' : '550px'
    },
    employeeList () {
      const r = new RegExp(this.searchEmployee, 'gi')
      return this.$store.getters.getEmployee.filter(item => {
        r.lastIndex = 0
        return r.test(item.full_name)
      })
    },
    user () {
      return this.$store.getters.getUser
    },
    englishLevelOfEmployee: {
      get () {
        return this.englishLevelOfSelectedEmployee
      },
      set (val) {
        this.englishLevelOfSelectedEmployee = val
      }
    }
  },
  mounted () {
    this.getRoles()
    if (!this.employeeList.length) {
      this.getEmployeeList()
    }
    this.getSkillsOfSelectedEmployee()
    this.getEnglishLevelsList()
  },
  methods: {
    getRoles () {
      this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.isHR = result.data.findIndex(item => item.RoleName === 'HR') > -1
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getEmployeeList () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    closeDialog () {
      this.currentValueToEdit = {
        employeeId: null,
        skillsId: null,
        subcategoryId: null,
        years: null,
        endYear: null,
        your_estimation: null,
        date_of_estimation: null,
        expertId: null,
        expert_full_name: '',
        expert_estimation: null,
        isPrimary: false
      }
      this.currentEnglishLevelEdit = {
        employeeId: null,
        categoryId: null,
        englishLevel: null,
        subcategoryId: null
      }
      this.searchEmployee = ''
      this.showExpert = false
      this.showEditDialog = false
      this.showEnglishLevelEditDialog = false
    },
    getSkillsOfSelectedEmployee (employee) {
      this.axios.get(`/api/employee_skills/skills/${this.employee.employeeId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (res.data && res.data.filter(item => item.Dev_Skills_Category_ID === 12)[0]) {
          const englishSkill = res.data.filter(item => item.Dev_Skills_Category_ID === 12)[0]
          this.englishLevelOfEmployee.employeeId = englishSkill.Employee_ID
          this.englishLevelOfEmployee.categoryId = englishSkill.Dev_Skills_Category_ID
          this.englishLevelOfEmployee.englishLevel = englishSkill.Dev_Skills_English_Level
          this.englishLevelOfEmployee.subcategoryId = englishSkill.Dev_Skills_Subcategory_ID
        } else {
          this.englishLevelOfEmployee.employeeId = this.employee.employeeId
          this.englishLevelOfEmployee.categoryId = 12
        }
        this.employeeSkillsToEdit = res.data.filter(item => item.Dev_Skills_Category_ID !== 12)
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getEnglishLevelsList () {
      this.axios.get('/api/employee_english_levels_list', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.englishLevels = result.data.map(item => {
          return {
            Dev_Skills_Subcategory_Description: item.Dev_Skills_Subcategory_Description,
            Dev_Skills_Subcategory_ID: item.Dev_Skills_Subcategory_ID
          }
        })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    saveSkillsGrid () {
      if (this.editMode) {
        this.axios.put(`/api/employee_skills/${this.employee.employeeId}`, this.employeeSkillsToEdit, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.show = false
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
      this.editMode = !this.editMode
    },
    closeSkillsEditorDialog () {
      this.editMode = false
      this.employeeSkillsToEdit = []
      this.englishLevelOfSelectedEmployee = {
        employeeId: null,
        categoryId: null,
        englishLevel: null,
        subcategoryId: null
      }
      this.show = false
    },
    englishLevelChange () {
      this.englishLevelOfEmployee.englishLevel = this.englishLevels.filter(item => item.Dev_Skills_Subcategory_ID === this.englishLevelOfEmployee.subcategoryId)[0].Dev_Skills_Subcategory_Description
      this.axios.post('/api/employee_english_skills', this.englishLevelOfEmployee, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'English level successfuly saved' })
      }).catch(error => {
        this.saveEnglishLevelDisable = false
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    expertChange (skill) {
      if (skill.Expert) {
        skill.Expert_ID = null
      } else {
        skill.Expert_FullName = this.employeeList.filter(item => item.employee_id === skill.Expert_ID)[0].full_name
      }
    },
    clearExpert (skill) {
      skill.Expert_ID = null
      skill.Expert_FullName = ''
    },
    changeExpertMode (skill) {
      if (skill.Expert) {
        skill.Expert_ID = null
        skill.Expert_FullName = ''
      } else {
        skill.Expert_FullName = ''
      }
    }
  },
  filters: {
    dateFilter (val) {
      return val ? new Date(val).toISOString().substr(0, 10) : ''
    }
  }
}
</script>
<style lang="scss" scoped>
  .english-level {
    font-size: 1rem;
    color: black;
    font-weight: 500;
  }
  .justify-content-center {
    justify-content: center;
  }
  .align-items-center {
    align-items: center;
  }
  .max-width-200 {
    max-width: 200px;
  }
  .margin-top--20px {
    margin-top: -20px;
  }
</style>
